import { useEffect, useState } from 'react';

import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ProfileAvatar } from '@/components/common/ProfileAvatar';
import { useLogout } from '@/hooks/useLogout';
import { useMe } from '@/hooks/useMe';

import { ProfileEditForm } from './ProfileEditForm';

export function ProfileCard() {
  const { me, isLoading, error, refetch } = useMe();
  const logout = useLogout();
  const [isEditing, setIsEditing] = useState(false);

  // If `me` reloads (e.g. after refetch) while the form is open, the form's
  // own local drafts win — but if `me` disappears entirely we close the form.
  useEffect(() => {
    if (!me) setIsEditing(false);
  }, [me]);

  if (isLoading) {
    return (
      <div
        className="bg-wolisho-card rounded-xl px-6 pt-8 pb-6 flex flex-col items-center mb-8"
        data-testid="account-profile-loading"
      >
        <LoadingSpinner label="Loading profile" testId="account-profile-spinner" />
      </div>
    );
  }

  if (error || !me) {
    return (
      <div className="mb-8" data-testid="account-profile-error">
        <ErrorState
          message="Failed to load your profile. Please try again."
          onPrimaryAction={() => void refetch()}
          testId="account-profile-error-state"
        />
      </div>
    );
  }

  return (
    <div
      className="bg-wolisho-card rounded-xl px-6 pt-8 pb-6 flex flex-col items-center mb-8"
      data-testid="account-profile"
    >
      <ProfileAvatar
        src={me.profilePictureLocation}
        firstName={me.name}
        lastName={me.surname}
        size={80}
      />
      {isEditing ? (
        <ProfileEditForm me={me} refetch={refetch} onDone={() => setIsEditing(false)} />
      ) : (
        <>
          <div className="mt-4 text-center w-full">
            <div className="flex items-center justify-center gap-2 min-w-0">
              <h1
                className="text-2xl font-bold text-black truncate min-w-0"
                data-testid="account-name"
                data-ph-mask
              >
                {me.fullName}
              </h1>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                data-testid="account-edit-profile"
                aria-label="Edit profile"
                className="shrink-0 p-2 flex items-center text-wolisho-brown opacity-40 hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-black/50 mt-1" data-testid="account-email" data-ph-mask>
              {me.email}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            data-testid="account-logout"
            className="text-wolisho-red font-bold text-sm pt-5 hover:opacity-80 transition-opacity"
          >
            Log out
          </button>
        </>
      )}
    </div>
  );
}
